import { AttendContactDomainService } from './domain';
import { AttendContactAbsenceFactory } from './entity/attendContactAbsenceFactory';
import { AttendContactLateFactory } from './entity/attendContactLateFactory';
import { AttendContactLeaveEarlyFactory } from './entity/attendContactLeaveEarlyFactory';

import type { AttendContactAbsenceIRepository } from './entity/attendContactAbsenceIRepository';
import type { AttendContactLateIRepository } from './entity/attendContactLateIRepository';
import type { AttendContactLeaveEarlyIRepository } from './entity/attendContactLeaveEarlyIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { AttendContactAbsenceJson, AttendContactAbsenceJsonPvt } from '@/types/domain/attendContact/entities/attendContactAbsenceEntity';
import type { AttendContactLateJson, AttendContactLateJsonPvt } from '@/types/domain/attendContact/entities/attendContactLateEntity';
import type { AttendContactLeaveEarlyJson, AttendContactLeaveEarlyJsonPvt } from '@/types/domain/attendContact/entities/attendContactLeaveEarlyEntity';

/**
 * ChildApplicationService
 */
export class AttendContactApplicationService {
  private service!: AttendContactDomainService;

  private absenceFactory = new AttendContactAbsenceFactory();

  private lateFactory = new AttendContactLateFactory();

  private leaveEarlyFactory = new AttendContactLeaveEarlyFactory();

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param absenceRepository absenceRepository
   * @param lateRepository lateRepository
   * @param leaveEarlyRepository leaveEarlyRepository
   * @param logger logger
   */
  public inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    absenceRepository: AttendContactAbsenceIRepository,
    lateRepository: AttendContactLateIRepository,
    leaveEarlyRepository: AttendContactLeaveEarlyIRepository,
    logger: Logger,
  ) {
    this.service = new AttendContactDomainService(logger);
    this.service.inject(
      wagakoNurseryApi,
      absenceRepository,
      lateRepository,
      leaveEarlyRepository,
    );
  }

  /**
   * getAttendContactAbsenceList
   * @param token トークン
   * @returns 結果
   */
  async getAttendContactAbsenceList(token: string | null) {
    const result = await this.service.getAttendContactAbsenceList(token);
    return result;
  }

  /**
   * getAttendContactLateList
   * @param token トークン
   * @returns 結果
   */
  async getAttendContactLateList(token: string | null) {
    const result = await this.service.getAttendContactLateList(token);
    return result;
  }

  /**
   * getAttendContactLeaveEarlyList
   * @param token トークン
   * @returns 結果
   */
  async getAttendContactLeaveEarlyList(token: string | null) {
    const result = await this.service.getAttendContactLeaveEarlyList(token);
    return result;
  }

  /**
   * sendNurseryAttendContactAbsence
   * @param input 入力
   */
  async sendNurseryAttendContactAbsence(
    input: AttendContactAbsenceJson,
  ) {
    const entity = this.absenceFactory.createFromJson(input);
    await this.service.sendNurseryAttendContactAbsence(entity);
  }

  /**
   * removeNurseryAttendContactAbsence
   * @param input 入力
   */
  async removeNurseryAttendContactAbsence(
    input: AttendContactAbsenceJson,
  ) {
    const entity = this.absenceFactory.createFromJson(input);
    await this.service.removeNurseryAttendContactAbsence(entity);
  }

  /**
   * sendNurseryAttendContactLate
   * @param input 入力
   */
  async sendNurseryAttendContactLate(
    input: AttendContactLateJson,
  ) {
    const entity = this.lateFactory.createFromJson(input);
    await this.service.sendNurseryAttendContactLate(entity);
  }

  /**
   * removeNurseryAttendContactLate
   * @param input 入力
   */
  async removeNurseryAttendContactLate(
    input: AttendContactLateJson,
  ) {
    const entity = this.lateFactory.createFromJson(input);
    await this.service.removeNurseryAttendContactLate(entity);
  }

  /**
   * sendNurseryAttendContactLeaveEarly
   * @param input 入力
   */
  async sendNurseryAttendContactLeaveEarly(
    input: AttendContactLeaveEarlyJson,
  ) {
    const entity = this.leaveEarlyFactory.createFromJson(input);
    await this.service.sendNurseryAttendContactLeaveEarly(entity);
  }

  /**
   * removeNurseryAttendContactLeaveEarly
   * @param input 入力
   */
  async removeNurseryAttendContactLeaveEarly(
    input: AttendContactLeaveEarlyJson,
  ) {
    const entity = this.leaveEarlyFactory.createFromJson(input);
    await this.service.removeNurseryAttendContactLeaveEarly(entity);
  }

  /**
   * 欠席リストを更新する(Create)
   * @param absenceList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createAbsenceStore(
    absenceList: AttendContactAbsenceJson[],
    payload: EntitySubscribeType,
    filter: ((json: AttendContactAbsenceJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      absenceList,
      payload,
      this.absenceFactory,
      filter,
    );
  }

  /**
   * 欠席リストを更新する(Update)
   * @param absenceList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateAbsenceStore(
    absenceList: AttendContactAbsenceJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      absenceList,
      payload,
      this.absenceFactory,
    );
  }

  /**
   * 欠席リストを更新する(Delete)
   * @param absenceList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteAbsenceStore(
    absenceList: AttendContactAbsenceJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      absenceList,
      payload,
      this.absenceFactory,
    );
  }

  /**
   * 遅刻リストを更新する(Create)
   * @param lateList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createLateStore(
    lateList: AttendContactLateJson[],
    payload: EntitySubscribeType,
    filter: ((json: AttendContactLateJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      lateList,
      payload,
      this.lateFactory,
      filter,
    );
  }

  /**
   * 遅刻リストを更新する(Update)
   * @param lateList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateLateStore(
    lateList: AttendContactLateJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      lateList,
      payload,
      this.lateFactory,
    );
  }

  /**
   * 遅刻リストを更新する(Delete)
   * @param lateList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteLateStore(
    lateList: AttendContactLateJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      lateList,
      payload,
      this.lateFactory,
    );
  }

  /**
   * 遅刻リストを更新する(Create)
   * @param leaveEarlyList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createLeaveEarlyStore(
    leaveEarlyList: AttendContactLeaveEarlyJson[],
    payload: EntitySubscribeType,
    filter: ((json: AttendContactLeaveEarlyJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      leaveEarlyList,
      payload,
      this.leaveEarlyFactory,
      filter,
    );
  }

  /**
   * 遅刻リストを更新する(Update)
   * @param leaveEarlyList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateLeaveEarlyStore(
    leaveEarlyList: AttendContactLeaveEarlyJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      leaveEarlyList,
      payload,
      this.leaveEarlyFactory,
    );
  }

  /**
   * 遅刻リストを更新する(Delete)
   * @param leaveEarlyList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteLeaveEarlyStore(
    leaveEarlyList: AttendContactLeaveEarlyJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      leaveEarlyList,
      payload,
      this.leaveEarlyFactory,
    );
  }
}
